import {
    BACKGROUND_MUSIC_URL, ENTER_SOUND_URL,
    FAIL_SOUND_URL, HINT_SOUND_URL, KEYBOARD_SOUND_URL, LETTER_ERROR_SOUND_URL,
    PLAY_BUTTON_SOUND_URL,
    SUCCESS_SOUND_URL
} from "../../common/constants.ts";

interface MusicPlayerState {
    isPlayMusic: boolean; // 是否可以播放音乐
    isPlayingMusic: boolean; // 是否正在播放音乐
    isPlaySound: boolean;
    volume: number;
    currentTime: number;
    duration: number;
    shareData: boolean;
}

interface AudioResource {
    buffer: AudioBuffer | null;
    sourceNode: AudioBufferSourceNode | null;
    gainNode: GainNode | null;
}

// 字符串枚举
enum SoundType {
    Enter = 'Enter',
    Fail = 'Fail',
    Hint = 'Hint',
    Keyboard = 'Keyboard',
    Error = 'LetterError',
    Success = 'Success',
    BackgroundMusic = 'BackgroundMusic',
    CommonBtn = 'CommonBtn'
}

class BackgroundMusicManager {
    private audioContext: AudioContext = new window.AudioContext();
    private audioResources: { [key: string]: AudioResource } = {};

    private state: MusicPlayerState = {
        isPlayMusic: true,
        isPlaySound: true,
        isPlayingMusic: false,
        volume: 0.5,
        currentTime: 0,
        duration: 0,
        shareData: true,
    };

    constructor() {
        this.initialize();
    }

    public async initialize(): Promise<void> {
        // 初始化并加载所有音频资源

        // 1.背景音乐
        await this.loadAudioResource(SoundType.BackgroundMusic, BACKGROUND_MUSIC_URL);
        // 2.通用按键音
        await this.loadAudioResource(SoundType.CommonBtn, PLAY_BUTTON_SOUND_URL);
        // 3.游戏胜利音
        await this.loadAudioResource(SoundType.Success, SUCCESS_SOUND_URL);
        // 4.游戏失败音
        await this.loadAudioResource(SoundType.Fail, FAIL_SOUND_URL);
        // 5.键盘字母按键音
        await this.loadAudioResource(SoundType.Keyboard, KEYBOARD_SOUND_URL);
        // 6.键盘enter按键音
        await this.loadAudioResource(SoundType.Enter, ENTER_SOUND_URL);
        // 7.键盘hint按键音
        await this.loadAudioResource(SoundType.Hint, HINT_SOUND_URL);
        // 8.错误提示音
        await this.loadAudioResource(SoundType.Error, LETTER_ERROR_SOUND_URL);
    }

    // 加载音频数据
    private async loadAudioResource(key: string, url: string): Promise<void> {
        try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

            this.audioResources[key] = {
                buffer: audioBuffer,
                sourceNode: null,
                gainNode: this.audioContext.createGain(),
            };

            // 设置音量
            this.audioResources[key].gainNode!.gain.value = this.state.volume;
        } catch (error) {
            console.error(`load music error：${url}`, error);
        }
    }

    // 播放通用按键音
    public playButtonSound() {
        if (!this.state.isPlaySound) return;
        this.playSound(SoundType.CommonBtn, false)
    }

    // 播放字母错误音效
    public playLetterErrorSound() {
        if (!this.state.isPlaySound) return;
        this.playSound(SoundType.Error, false)
    }

    public playKeyboardSound(): void {
        if (!this.state.isPlaySound) return;
        this.playSound(SoundType.Keyboard, false)
    }

    // 播放hint提示音
    public playHintSound() {
        if (!this.state.isPlaySound) return;
        this.playSound(SoundType.Hint, false)
    }

    // 播放enter音效
    public playEnterSound() {
        if (!this.state.isPlaySound) return;
        this.playSound(SoundType.Enter, false)
    }

    // 播放游戏失败音效
    public playFailSound() {
        if (!this.state.isPlaySound) return;
        this.playSound(SoundType.Fail, false)
    }

    // 播放游戏成功音效
    public playSuccessSound() {
        this.playSound(SoundType.Success, false)
    }

    // 播放背景音乐
    public playMusic() {
        if (!this.state.isPlayMusic) return;
        if (this.state.isPlayingMusic) return;
        this.playSound(SoundType.BackgroundMusic, true)
        this.state.isPlayingMusic = true
    }

    // 停止播放背景音乐
    public pauseMusic() {
        const resource = this.audioResources[SoundType.BackgroundMusic];
        if (resource.sourceNode) {
            resource.sourceNode.stop();
            resource.sourceNode.disconnect();
            resource.sourceNode = null;
            this.state.isPlayingMusic = false;
        }
    }

    public playSound(key: string, loop?: boolean): void {
        const resource = this.audioResources[key];
        if (!resource || !resource.buffer) return;

        // 创建新的 source 和 gain 节点
        const sourceNode = this.audioContext.createBufferSource();
        const gainNode = this.audioContext.createGain();

        sourceNode.buffer = resource.buffer;
        sourceNode.connect(gainNode);
        // 如果设置为循环播放
        if (loop) {
            sourceNode.loop = true;
        }
        gainNode.connect(this.audioContext.destination);

        // 设置音量
        gainNode.gain.value = this.state.volume;

        // 播放
        sourceNode.start(0);

        // 更新资源中的节点（如果需要）
        resource.sourceNode = sourceNode;
        resource.gainNode = gainNode;
    }

    public stopSound(key: string): void {
        const resource = this.audioResources[key];
        if (!resource) return;

        if (resource.sourceNode) {
            resource.sourceNode.stop();
            resource.sourceNode.disconnect();
            resource.sourceNode = null;
        }
    }

    public setVolume(volume: number): void {
        this.state.volume = volume;

        // 更新所有 gain nodes 的音量
        Object.values(this.audioResources).forEach(resource => {
            if (resource.gainNode) {
                resource.gainNode.gain.value = volume;
            }
        });
    }

    public setState(state: MusicPlayerState) {
        this.state = {...state};
    }

    public getState(): MusicPlayerState {
        return {...this.state};
    }
}

export default BackgroundMusicManager;